import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, LinearProgress, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { bloomberg } from '../theme/bloombergTheme';
import { Attribution, AttributionDriver, Explanation, getAttribution, startExplain, getExplainStatus } from '../services/api';

/**
 * Why an Active portfolio is ahead of or behind its benchmark.
 * The breakdown is arithmetic (instant): each stock's contribution to the gap
 * = (portfolio weight − benchmark weight) × (stock return − benchmark return).
 * "Explain with AI" asks GPT-6 Luna (OpenAI) to write it up from those same
 * figures. A background job with polling, so a slow answer never blocks the page.
 */

const MONO = bloomberg.font.mono;
const eyebrow = { fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: bloomberg.text.muted };
const AI = '#a855f7';
const modelLabel = (m?: string) => (!m ? 'AI model' : m.startsWith('gpt-6-luna') ? 'GPT-6 Luna (OpenAI)'
  : m.startsWith('gpt') ? `${m} (OpenAI)` : `In-house model (${m.split(':')[0]})`);
const pct = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)}%`;
const pts = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)} pts`;
const shortName = (n: string) => n.replace(/,?\s+(Inc\.?|Incorporated|Corporation|Corp\.?|Holdings?|N\.V\.|plc|PLC|Ltd\.?|Limited|S\.A\.|SE|AG|Group)$/g, '');
const dateLabel = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const BUCKETS: Record<string, string> = {
  more_of_winners: 'More of the winners',
  less_of_winners: 'Less of (or missing) the winners',
  more_of_laggards: 'More of the laggards',
  less_of_laggards: 'Less of the laggards',
};

/** What the portfolio did with the stock. Against an equal-weight theme the benchmark
 *  weight is the same for every name, so "13% vs 5%" says nothing; the decision does. */
function position(x: AttributionDriver, etf: boolean): string {
  const w = `${(x.weight * 100).toFixed(0)}%`;
  if (!x.in_portfolio) return 'Not held';
  if (etf) return `Held at ${w}`;
  if (!x.in_benchmark) return `Held at ${w}, not in the benchmark`;
  const ratio = x.benchmark_weight ? x.weight / x.benchmark_weight : 1;
  if (ratio >= 1.2) return `Overweight at ${w}`;
  if (ratio <= 0.8) return `Underweight at ${w}`;
  return `Near benchmark weight, ${w}`;
}

function DriverList({ title, rows, etf, colour, rb }: { title: string; rows: AttributionDriver[]; etf: boolean; colour: string; rb: number }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ ...eyebrow, color: colour, mb: 0.5 }}>{title}</Typography>
      {rows.length === 0 && <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>Nothing material.</Typography>}
      {rows.map((x) => (
        <Box key={x.symbol} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', gap: 1, alignItems: 'baseline', py: 0.4, borderTop: `1px solid ${bloomberg.border.primary}` }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 12.5, color: bloomberg.text.primary }} title={x.name}>{shortName(x.name)}</Typography>
            <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>
              {position(x, etf)} · {x.return > rb ? 'beat' : 'lagged'} the benchmark by {Math.abs((x.return - rb) * 100).toFixed(0)} pts
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: MONO, fontSize: 12, color: bloomberg.text.secondary }}>{pct(x.return)}</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700, color: colour, minWidth: 70, textAlign: 'right' }}>{pts(x.contribution)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function WhyPanel({ portfolioId, updatedAt }: { portfolioId: number; updatedAt?: string }) {
  const [att, setAtt] = useState<Attribution | null>(null);
  const [attError, setAttError] = useState(false);
  const [ex, setEx] = useState<Explanation | null>(null);
  const poll = useRef<number | null>(null);

  const stopPoll = () => { if (poll.current) { window.clearInterval(poll.current); poll.current = null; } };
  const follow = (e: Explanation) => {
    setEx(e);
    stopPoll();
    if (e.status === 'running') {
      poll.current = window.setInterval(() => {
        getExplainStatus(portfolioId).then((s) => {
          if (s.status !== 'running') { stopPoll(); setEx((prev) => ({ ...prev, ...s })); }
        }).catch(() => {});
      }, 2000);
    }
  };

  useEffect(() => {
    setAtt(null); setAttError(false); setEx(null); stopPoll();
    getAttribution(portfolioId).then(setAtt).catch(() => setAttError(true));
    // A write-up may already be running or finished (started earlier, or on another visit).
    getExplainStatus(portfolioId).then((s) => { if (s.status !== 'idle') follow(s); }).catch(() => {});
    return stopPoll;
  }, [portfolioId, updatedAt]);

  const run = (refresh = false) => {
    setEx({ status: 'running', started: new Date().toISOString() });
    startExplain(portfolioId, refresh).then(follow)
      .catch((err) => setEx({ status: 'error', error: err?.response?.data?.detail || 'Could not start the explanation.' }));
  };

  if (attError) return null;
  if (!att) return <LinearProgress sx={{ mt: 1.5, height: 2 }} />;

  const etf = att.benchmark.kind === 'etf';
  const helped = att.drivers.filter((x) => x.contribution > 0.0005).slice(0, 4);
  const hurt = att.drivers.filter((x) => x.contribution < -0.0005).slice(0, 4);
  const pattern = etf ? [] : Object.entries(att.buckets).filter(([, v]) => Math.abs(v) >= 0.005).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  const period = att.window === 'since'
    ? `since ${att.start ? dateLabel(att.start) : 'the start'}${att.days != null ? ` · ${att.days} trading days` : ''}`
    : 'last 3 months · backtest of today\'s weights';

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${bloomberg.border.primary}` }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: bloomberg.text.primary }}>Why the difference</Typography>
        <Tooltip title="A stock moves the gap when you hold more or less of it than the benchmark does and it performs differently from the benchmark. Its share = (weight here − weight in the benchmark) × (its return − the benchmark's return). Buy-and-hold over the period, so the total can differ slightly from the figure above.">
          <Typography sx={{ fontSize: 12, color: bloomberg.text.muted, cursor: 'help' }}>
            {period} · {pct(att.portfolio_return)} vs {pct(att.benchmark_return)} = <b style={{ color: att.difference >= 0 ? bloomberg.status.positive : bloomberg.status.negative }}>{pts(att.difference)}</b>
          </Typography>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5 }}>
        <DriverList title="Helped most" rows={helped} etf={etf} colour={bloomberg.status.positive} rb={att.benchmark_return} />
        <DriverList title="Hurt most" rows={hurt} etf={etf} colour={bloomberg.status.negative} rb={att.benchmark_return} />
      </Box>

      {(pattern.length > 0 || att.sectors.length > 0) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
          {pattern.map(([k, v]) => (
            <Box key={k} sx={{ fontSize: 11.5, px: 1, py: 0.3, borderRadius: 1, border: `1px solid ${bloomberg.border.primary}`, color: bloomberg.text.secondary }}>
              {BUCKETS[k]} <b style={{ fontFamily: MONO, color: v >= 0 ? bloomberg.status.positive : bloomberg.status.negative }}>{pts(v)}</b>
            </Box>
          ))}
          {att.sectors.slice(0, 3).filter((sct) => Math.abs(sct.contribution) >= 0.005).map((sct) => (
            <Box key={sct.sector} sx={{ fontSize: 11.5, px: 1, py: 0.3, borderRadius: 1, border: `1px solid ${bloomberg.border.primary}`, color: bloomberg.text.secondary }}>
              {sct.sector} {(sct.weight * 100).toFixed(0)}% vs {(sct.benchmark_weight * 100).toFixed(0)}%{' '}
              <b style={{ fontFamily: MONO, color: sct.contribution >= 0 ? bloomberg.status.positive : bloomberg.status.negative }}>{pts(sct.contribution)}</b>
            </Box>
          ))}
        </Box>
      )}
      {att.unpriced.length > 0 && (
        <Typography sx={{ fontSize: 11, color: bloomberg.text.muted, mt: 0.75 }}>No prices for {att.unpriced.join(', ')}, left out.</Typography>
      )}

      {/* AI write-up */}
      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: `${AI}0f`, border: `1px solid ${AI}40` }}>
        {(!ex || ex.status === 'idle') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button size="small" variant="contained" disableElevation startIcon={<AutoAwesomeIcon />} onClick={() => run(false)}
              sx={{ textTransform: 'none', bgcolor: AI, '&:hover': { bgcolor: AI, filter: 'brightness(1.1)' } }}>
              Explain with AI
            </Button>
            <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>
              Written by GPT-6 Luna (OpenAI) from the figures above, with recent headlines for the biggest movers. Takes about 10 seconds.
            </Typography>
          </Box>
        )}
        {ex?.status === 'running' && (
          <Box>
            <Typography sx={{ fontSize: 12.5, color: bloomberg.text.secondary, mb: 0.75 }}>
              Writing the explanation. This usually takes about 10 seconds.
            </Typography>
            <LinearProgress sx={{ height: 3, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: AI } }} />
          </Box>
        )}
        {ex?.status === 'error' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, color: bloomberg.text.secondary }}>{ex.error}</Typography>
            <Button size="small" onClick={() => run(true)} sx={{ textTransform: 'none' }}>Try again</Button>
          </Box>
        )}
        {ex?.status === 'done' && ex.narrative && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <AutoAwesomeIcon sx={{ fontSize: 15, color: AI }} />
              <Typography sx={{ ...eyebrow, color: AI }}>AI explanation</Typography>
            </Box>
            {ex.narrative.split(/\n\s*\n/).map((para, i) => (
              <Typography key={i} sx={{ fontSize: 13, color: bloomberg.text.primary, lineHeight: 1.65, mb: 1 }}>{para}</Typography>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>
                {modelLabel(ex.model)} · from the figures above
                {ex.headlines && Object.keys(ex.headlines).length ? ` and headlines on ${Object.keys(ex.headlines).join(', ')}` : ''}
                {ex.generated_at ? ` · ${new Date(ex.generated_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                {' '}· check key figures against the breakdown.
              </Typography>
              <Button size="small" onClick={() => run(true)} sx={{ textTransform: 'none', fontSize: 11, minWidth: 0, py: 0 }}>Regenerate</Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
